import React, { useState, useEffect } from 'react';
import {
  Menu,
  MenuItem,
  Box,
  Typography,
  Divider,
  Avatar,
  Chip,
  Badge,
  IconButton,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Button
} from '@mui/material';
import {
  ShoppingBag as ShoppingBagIcon,
  CheckCircle as CheckCircleIcon,
  LocalShipping as LocalShippingIcon,
  Restaurant as RestaurantIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  LocalMall as LocalMallIcon
} from '@mui/icons-material';
import axios from 'axios';

const resolveImageUrl = (imagePath) => {
  const raw = String(imagePath || '').trim();
  if (!raw) return '';

  // If backend already returns an absolute URL
  if (/^https?:\/\//i.test(raw)) return raw;

  const base = String(axios.defaults.baseURL || 'http://localhost:3100').replace(/\/+$/, '');
  const path = raw.replace(/^\/+/, '');
  return `${base}/${encodeURI(path)}`;
};

const OrdersDropdown = ({ anchorEl, open, onClose, storeSlug, customerPhone }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [nowTick, setNowTick] = useState(Date.now());

  useEffect(() => {
    if (open && customerPhone) {
      loadOrders();
    }
  }, [open, customerPhone]);

  useEffect(() => {
    if (!open || !selectedOrder) return;
    if (selectedOrder.status !== 'PREPARING') return;
    const t = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(t);
  }, [open, selectedOrder]);

  const formatDuration = (ms) => {
    const totalSeconds = Math.max(0, Math.floor(ms / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  const getStatusTime = (order, status) => {
    if (!order) return null;
    const directMap = {
      CONFIRMED: order.confirmedAt,
      PREPARING: order.preparingAt,
      READY: order.readyAt,
      OUT_FOR_DELIVERY: order.outForDeliveryAt,
      DELIVERED: order.deliveredAt
    };
    const direct = directMap[status];
    if (direct) return new Date(direct);
    const hist = (order.statusHistory || []).find((h) => h?.status === status);
    return hist?.timestamp ? new Date(hist.timestamp) : null;
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `/api/ecommerce-store/public/${storeSlug}/orders/${customerPhone}`,
        {
          params: { _ts: Date.now() },
          headers: { 'x-skip-cache': 'true' }
        }
      );
      if (response.data.success) {
        const ordersData = response.data.data || [];
        setOrders(ordersData);
      }
    } catch (err) {
      console.error('Failed to load orders:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      'PENDING': 'warning',
      'PENDING_COUNTER_CONFIRM': 'warning',
      'PENDING_PAYMENT': 'warning',
      'CONFIRMED': 'info',
      'PREPARING': 'info',
      'READY': 'success',
      'OUT_FOR_DELIVERY': 'primary',
      'DELIVERED': 'success',
      'CANCELLED': 'error'
    };
    return statusMap[status] || 'default';
  };

  const getStatusIcon = (status) => {
    const iconMap = {
      'PENDING': <ScheduleIcon fontSize="small" />,
      'PENDING_COUNTER_CONFIRM': <ScheduleIcon fontSize="small" />,
      'PENDING_PAYMENT': <ScheduleIcon fontSize="small" />,
      'CONFIRMED': <CheckCircleIcon fontSize="small" />,
      'PREPARING': <RestaurantIcon fontSize="small" />,
      'READY': <CheckCircleIcon fontSize="small" />,
      'OUT_FOR_DELIVERY': <LocalShippingIcon fontSize="small" />,
      'DELIVERED': <LocalMallIcon fontSize="small" />,
      'CANCELLED': <CancelIcon fontSize="small" />
    };
    return iconMap[status] || <ShoppingBagIcon fontSize="small" />;
  };

  const getStatusLabel = (status) => {
    const labelMap = {
      'PENDING': 'Pending',
      'PENDING_COUNTER_CONFIRM': 'Pending Confirmation',
      'PENDING_PAYMENT': 'Pending Payment',
      'CONFIRMED': 'Confirmed',
      'PREPARING': 'Preparing',
      'READY': 'Ready',
      'OUT_FOR_DELIVERY': 'Out for Delivery',
      'DELIVERED': 'Delivered',
      'CANCELLED': 'Cancelled'
    };
    return labelMap[status] || status;
  };

  const getActiveStep = (status) => {
    const stepMap = {
      'PENDING': 0,
      'PENDING_COUNTER_CONFIRM': 0,
      'PENDING_PAYMENT': 0,
      'CONFIRMED': 1,
      'PREPARING': 2,
      'READY': 3,
      'OUT_FOR_DELIVERY': 4,
      'DELIVERED': 5,
      'CANCELLED': -1
    };
    return stepMap[status] ?? 0;
  };

  const statusSteps = [
    { label: 'Placed', key: 'PENDING' },
    { label: 'Confirmed', key: 'CONFIRMED' },
    { label: 'Preparing', key: 'PREPARING' },
    { label: 'Ready', key: 'READY' },
    { label: 'Out for Delivery', key: 'OUT_FOR_DELIVERY' },
    { label: 'Delivered', key: 'DELIVERED' }
  ];

  // Custom StepIcon component to show icons instead of numbers
  const CustomStepIcon = (props) => {
    const { active, completed, icon } = props;
    const stepIndex = icon - 1; // icon is 1-indexed
    const step = statusSteps[stepIndex];
    
    const iconMap = {
      'PENDING': <ShoppingBagIcon />,
      'CONFIRMED': <CheckCircleIcon />,
      'PREPARING': <RestaurantIcon />,
      'READY': <CheckCircleIcon />,
      'OUT_FOR_DELIVERY': <LocalShippingIcon />,
      'DELIVERED': <LocalMallIcon />
    };
    
    return (
      <Box
        sx={{
          color: completed || active ? 'primary.main' : 'grey.400',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        {iconMap[step?.key] || <ShoppingBagIcon />}
      </Box>
    );
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(selectedOrder?._id === order._id ? null : order);
  };

  const handleBackToList = () => {
    setSelectedOrder(null);
  };

  if (loading) {
    return (
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        PaperProps={{
          sx: {
            width: 420,
            maxHeight: 600,
            mt: 1.5
          }
        }}
      >
        <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      </Menu>
    );
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 420,
          maxHeight: 600,
          mt: 1.5,
          overflow: 'hidden'
        }
      }}
    >
      {selectedOrder ? (
        // Order Details View
        <Box>
          {/* Header */}
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1, borderBottom: 1, borderColor: 'divider' }}>
            <IconButton size="small" onClick={handleBackToList}>
              <CloseIcon />
            </IconButton>
            <Box sx={{ flex: 1 }}>
              <Typography variant="subtitle2" fontWeight={700}>
                Order #{selectedOrder._id.slice(-8).toUpperCase()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatDate(selectedOrder.createdAt)}
              </Typography>
            </Box>
            <Chip
              label={getStatusLabel(selectedOrder.status)}
              color={getStatusColor(selectedOrder.status)}
              size="small"
              icon={getStatusIcon(selectedOrder.status)}
            />
          </Box>

          {/* Tracking Status */}
          <Box sx={{ p: 2, maxHeight: 480, overflow: 'auto' }}>
            {selectedOrder.status === 'CANCELLED' ? (
              <Box sx={{ p: 2, bgcolor: 'error.light', borderRadius: 1, mb: 2 }}>
                <Typography variant="body2" fontWeight={600} color="error.dark">
                  Order Cancelled
                </Typography>
                {selectedOrder.cancellationReason && (
                  <Typography variant="caption" color="error.dark">
                    Reason: {selectedOrder.cancellationReason}
                  </Typography>
                )}
              </Box>
            ) : (
              <Stepper activeStep={getActiveStep(selectedOrder.status)} orientation="vertical" sx={{ mb: 2 }}>
                {statusSteps.map((step, index) => (
                  <Step key={step.key}>
                    <StepLabel StepIconComponent={CustomStepIcon}>
                      <Typography variant="body2" fontWeight={index <= getActiveStep(selectedOrder.status) ? 600 : 400}>
                        {step.label}
                      </Typography>
                      
                      {/* Bill info below Confirmed step */}
                      {step.key === 'CONFIRMED' && index <= getActiveStep(selectedOrder.status) && 
                       (selectedOrder.billNo || selectedOrder.kotNo || selectedOrder.tokenNo) && (
                        <Box sx={{ mt: 0.5 }}>
                          {(() => {
                            const t = getStatusTime(selectedOrder, 'CONFIRMED');
                            return t ? (
                              <Typography variant="caption" color="text.secondary" display="block">
                                Bill Time: {formatDate(t)}
                              </Typography>
                            ) : null;
                          })()}
                          {selectedOrder.billNo && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Bill: {selectedOrder.billNo}
                            </Typography>
                          )}
                          {selectedOrder.tokenNo && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Token: {selectedOrder.tokenNo}
                            </Typography>
                          )}
                          {selectedOrder.kotNo && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              KOT: {selectedOrder.kotNo}
                            </Typography>
                          )}
                        </Box>
                      )}

                      {/* Cooking timer below Preparing step */}
                      {step.key === 'PREPARING' && selectedOrder.status === 'PREPARING' && (
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                          Cooking time: {(() => {
                            const start = getStatusTime(selectedOrder, 'PREPARING');
                            return start ? formatDuration(nowTick - start.getTime()) : '00:00:00';
                          })()}
                        </Typography>
                      )}

                      {/* Timestamp and delivery boy below Ready/Out for Delivery/Delivered steps */}
                      {(step.key === 'READY' || step.key === 'OUT_FOR_DELIVERY' || step.key === 'DELIVERED') && (
                        <>
                          {(() => {
                            const statusTime = getStatusTime(selectedOrder, step.key);
                            if (statusTime) {
                              return (
                                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                                  {formatDate(statusTime)}
                                </Typography>
                              );
                            }
                            return null;
                          })()}
                          {(selectedOrder.status === 'READY' || selectedOrder.status === 'OUT_FOR_DELIVERY' || selectedOrder.status === 'DELIVERED') &&
                           selectedOrder.deliveryPerson?.name && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                              Delivery Boy: {selectedOrder.deliveryPerson.name}
                            </Typography>
                          )}
                        </>
                      )}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
            )}

            <Divider sx={{ my: 2 }} />

            {/* Order Items */}
            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Order Items
            </Typography>
            {selectedOrder.items?.map((item, idx) => (
              <Box key={idx} sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                <Avatar
                  variant="rounded"
                  src={resolveImageUrl(item.image)}
                  sx={{ width: 48, height: 48, bgcolor: 'grey.200' }}
                >
                  <RestaurantIcon />
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" fontWeight={600}>
                    {item.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Qty: {item.qty} × ${item.price?.toFixed(2)}
                  </Typography>
                </Box>
                <Typography variant="body2" fontWeight={600}>
                  ${(item.qty * item.price)?.toFixed(2)}
                </Typography>
              </Box>
            ))}

            <Divider sx={{ my: 2 }} />

            {/* Order Summary */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="body2">Subtotal</Typography>
              <Typography variant="body2">${selectedOrder.subTotal?.toFixed(2)}</Typography>
            </Box>
            {selectedOrder.deliveryCharge > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Delivery</Typography>
                <Typography variant="body2">${selectedOrder.deliveryCharge?.toFixed(2)}</Typography>
              </Box>
            )}
            {selectedOrder.taxAmount > 0 && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Tax</Typography>
                <Typography variant="body2">${selectedOrder.taxAmount?.toFixed(2)}</Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body1" fontWeight={700}>Total</Typography>
              <Typography variant="body1" fontWeight={700}>${selectedOrder.total?.toFixed(2)}</Typography>
            </Box>

            {/* Delivery Info */}
            {selectedOrder.customer && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                  Delivery Information
                </Typography>
                {selectedOrder.customer.name && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Name:</strong> {selectedOrder.customer.name}
                  </Typography>
                )}
                {selectedOrder.customer.phone && (
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    <strong>Phone:</strong> {selectedOrder.customer.phone}
                  </Typography>
                )}
                {selectedOrder.customer.address && (
                  <Typography variant="body2">
                    <strong>Address:</strong> {selectedOrder.customer.address}
                  </Typography>
                )}
              </>
            )}
          </Box>
        </Box>
      ) : (
        // Orders List View
        <Box>
          {/* Header */}
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="h6" fontWeight={700}>
              My Orders
            </Typography>
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Orders List */}
          <Box sx={{ maxHeight: 520, overflow: 'auto' }}>
            {orders.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <ShoppingBagIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  No orders yet
                </Typography>
              </Box>
            ) : (
              orders.map((order) => (
                <MenuItem
                  key={order._id}
                  onClick={() => handleOrderClick(order)}
                  sx={{
                    p: 2,
                    display: 'block',
                    borderBottom: 1,
                    borderColor: 'divider',
                    '&:hover': { bgcolor: 'action.hover' }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, mb: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={700}>
                        Order #{order._id.slice(-8).toUpperCase()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDate(order.createdAt)}
                      </Typography>
                    </Box>
                    <Chip
                      label={getStatusLabel(order.status)}
                      color={getStatusColor(order.status)}
                      size="small"
                      icon={getStatusIcon(order.status)}
                    />
                  </Box>

                  {/* Order Items Preview */}
                  <Box sx={{ display: 'flex', gap: 1, mb: 1, overflow: 'hidden' }}>
                    {order.items?.slice(0, 3).map((item, idx) => (
                      <Avatar
                        key={idx}
                        variant="rounded"
                        src={resolveImageUrl(item.image)}
                        sx={{ width: 40, height: 40, bgcolor: 'grey.200' }}
                      >
                        <RestaurantIcon fontSize="small" />
                      </Avatar>
                    ))}
                    {order.items?.length > 3 && (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: 'grey.200',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <Typography variant="caption" fontWeight={600}>
                          +{order.items.length - 3}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      {order.items?.length} item(s)
                    </Typography>
                    <Typography variant="body2" fontWeight={700}>
                      ${order.total?.toFixed(2)}
                    </Typography>
                  </Box>
                </MenuItem>
              ))
            )}
          </Box>
        </Box>
      )}
    </Menu>
  );
};

export default OrdersDropdown;
